import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { Heart, MessageCircle, Gift, Lock, Trash2, BadgeCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, mediaSrc, startCheckout } from "../../lib/api";
import { Comments } from "./Comments";

const Media = ({ post }) => {
  const base = mediaSrc(post.media_url);
  const [retry, setRetry] = useState(0);
  const src = base && retry ? `${base}?r=${retry}` : base;
  const onError = async () => {
    if (retry >= 2) return;
    try { await api.post("/auth/refresh"); } catch (e) { /* session may be gone */ }
    setRetry((n) => n + 1);
  };
  const [buying, setBuying] = useState(false);
  const buy = async () => {
    setBuying(true);
    try {
      await startCheckout({ kind: "ppv", post_id: post.id });
    } catch (e) {
      toast.error(formatError(e));
      setBuying(false);
    }
  };
  if (post.locked) {
    return (
      <div data-testid="feed-locked-media" className="relative aspect-[4/5] sm:aspect-[4/3] overflow-hidden bg-[#E6F3FC]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(215,38,61,0.25),transparent_55%),radial-gradient(circle_at_70%_70%,rgba(79,163,232,0.55),transparent_60%)] blur-2xl" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center px-6">
          <span className="w-16 h-16 rounded-full border border-[#D7263D]/60 grid place-items-center"><Lock className="w-6 h-6 text-[#D7263D]" /></span>
          {post.media_type === "video" && <span data-testid="feed-locked-video-label" className="font-mono-x text-[0.62rem] tracking-[0.2em] uppercase text-[#4A5878]">Video</span>}
          {post.lock_reason === "ppv" ? (
            <>
              <p className="font-display text-2xl text-[#0E1B3D]">Pay-per-view post</p>
              <button data-testid="feed-ppv-unlock-btn" disabled={buying} onClick={buy} className="gold-btn px-6 py-3 text-xs font-semibold tracking-[0.16em] uppercase disabled:opacity-60">
                {buying ? "Opening checkout…" : `Unlock for $${post.ppv_price}`}
              </button>
            </>
          ) : (
            <>
              <p className="font-display text-2xl text-[#0E1B3D]">For {post.tier_name} members</p>
              <Link to={`/join?tier=${post.tier}`} data-testid="feed-upgrade-tier-cta" className="gold-btn px-6 py-3 text-xs font-semibold tracking-[0.16em] uppercase">Upgrade to unlock</Link>
            </>
          )}
        </div>
      </div>
    );
  }
  if (!src) return null;
  if (post.media_type === "video") return <video data-testid="feed-post-video" src={src} onError={onError} controls playsInline preload="metadata" className="w-full max-h-[80vh] bg-black" />;
  return <img data-testid="feed-post-image" src={src} onError={onError} alt="" loading="lazy" className="w-full max-h-[85vh] object-cover" />;
};

const EditForm = ({ post, onSaved, onCancel }) => {
  const [caption, setCaption] = useState(post.caption || "");
  const [tier, setTier] = useState(post.tier);
  const [ppv, setPpv] = useState(post.ppv_price ?? "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      let media_id;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        media_id = (await api.post("/admin/media", fd)).data.media_id;
      }
      const { data } = await api.patch(`/admin/posts/${post.id}`, { caption, tier, ppv_price: ppv ? Number(ppv) : null, ...(media_id && { media_id }) });
      toast.success("Post updated");
      onSaved(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  const field = "bg-[#FFF8EE] border border-[#0E1B3D]/15 h-10 px-3 text-sm text-[#0E1B3D]";
  return (
    <div data-testid="admin-edit-post-form" className="px-5 sm:px-7 pb-4 flex flex-col gap-3">
      <textarea data-testid="admin-edit-caption-input" rows={3} value={caption} maxLength={2000} onChange={(e) => setCaption(e.target.value)} placeholder="Caption"
        className="w-full bg-[#FFF8EE] border border-[#0E1B3D]/15 focus:border-[#D7263D] outline-none p-3 text-sm text-[#0E1B3D] resize-none" />
      <div className="flex flex-wrap gap-3 items-center">
        <select data-testid="admin-edit-tier-select" value={tier} onChange={(e) => setTier(e.target.value)} className={field}>
          <option value="fan">Fan and above</option>
          <option value="vip">VIP and above</option>
          <option value="inner">Inner Circle only</option>
        </select>
        <input data-testid="admin-edit-ppv-input" type="number" min="3" max="1000" step="1" value={ppv} onChange={(e) => setPpv(e.target.value)} placeholder="PPV $ (empty = none)" className={`${field} w-44`} />
        <label data-testid="admin-edit-replace-media-btn" className="ghost-btn h-10 px-4 text-xs tracking-[0.14em] uppercase grid place-items-center cursor-pointer">
          {file ? file.name.slice(0, 18) : "Replace media"}
          <input data-testid="admin-edit-media-input" type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      </div>
      <div className="flex gap-3 items-center">
        <button data-testid="admin-edit-save-btn" disabled={busy} onClick={save} className="gold-btn px-5 h-10 text-xs font-semibold tracking-[0.14em] uppercase disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
        <button data-testid="admin-edit-cancel-btn" onClick={onCancel} className="text-xs tracking-[0.14em] uppercase text-[#4A5878] hover:text-[#0E1B3D]">Cancel</button>
      </div>
    </div>
  );
};

export function PostCard({ post: initial, isAdmin, onTip, onDeleted }) {
  const [post, setPost] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.like_count);
  const [comments, setComments] = useState(post.comment_count);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setPost(initial);
    setLiked(initial.liked);
    setLikes(initial.like_count);
    setComments(initial.comment_count);
  }, [initial]);

  const like = async () => {
    setLiked(!liked);
    setLikes((n) => n + (liked ? -1 : 1));
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikes(data.like_count);
    } catch (e) {
      setLiked(liked);
      setLikes(post.like_count);
      toast.error(formatError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/posts/${post.id}`);
      onDeleted?.(post.id);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <motion.article data-testid="feed-post-card" initial={{ opacity: 0.3, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="border border-[#0E1B3D]/[0.08] bg-[#FFFFFF] overflow-hidden">
      <header className="flex items-start gap-3 px-5 sm:px-7 py-4">
        <img src="/img/avatar.jpg" alt="Katie" className="w-10 h-10 rounded-full object-cover ring-1 ring-[#D7263D]/50 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0E1B3D] flex items-center gap-1.5 whitespace-nowrap">Katie Razenski <BadgeCheck className="w-4 h-4 text-[#D7263D] shrink-0" /></p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="text-xs text-[#4A5878]">{post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : ""}</span>
            {post.ppv_price && <span data-testid="feed-post-ppv-badge" className="font-mono-x text-[0.6rem] tracking-[0.18em] uppercase bg-[#D7263D] text-[#FFF8EE] px-2 py-0.5 whitespace-nowrap">{post.purchased ? "Unlocked" : `PPV $${post.ppv_price}`}</span>}
            <span data-testid="feed-post-tier" className="font-mono-x text-[0.6rem] tracking-[0.18em] uppercase border border-[#D7263D]/40 text-[#D7263D] px-2 py-0.5 whitespace-nowrap">{post.tier_name}</span>
          </div>
        </div>
        {isAdmin && <button data-testid="admin-edit-post-btn" onClick={() => setEditing(!editing)} className="p-2 shrink-0 text-[#4A5878] hover:text-[#D7263D]" aria-label="Edit post"><Pencil className="w-4 h-4" /></button>}
        {isAdmin && <button data-testid="admin-delete-post-btn" onClick={remove} className="p-2 shrink-0 text-[#4A5878] hover:text-[#C81E3A]" aria-label="Delete post"><Trash2 className="w-4 h-4" /></button>}
      </header>
      {editing && <EditForm post={post} onSaved={(p) => { setPost(p); setEditing(false); }} onCancel={() => setEditing(false)} />}
      {!editing && post.caption && <p data-testid="feed-post-caption" className="px-5 sm:px-7 pb-4 text-[0.95rem] text-[#26355A] leading-relaxed whitespace-pre-line">{post.caption}</p>}
      <Media post={post} />
      {!post.locked && (
        <div className="flex items-center gap-6 px-5 sm:px-7 py-4">
          <motion.button whileTap={{ scale: 0.8 }} data-testid="feed-like-button" onClick={like} className="flex items-center gap-2 text-sm text-[#26355A] hover:text-[#0E1B3D]">
            <Heart className={`w-5 h-5 transition-colors ${liked ? "fill-[#E63946] text-[#E63946]" : ""}`} /><span data-testid="feed-like-count">{likes}</span>
          </motion.button>
          <button data-testid="feed-comment-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm text-[#26355A] hover:text-[#0E1B3D]">
            <MessageCircle className="w-5 h-5" /><span>{comments}</span>
          </button>
          {post.tips && (
            <span data-testid="feed-post-tips-total" className="ml-auto flex items-center gap-1.5 text-sm text-[#D7263D] whitespace-nowrap">
              <Gift className="w-5 h-5" /> ${post.tips.total.toFixed(0)} <span className="text-xs text-[#4A5878]">from {post.tips.count} tip{post.tips.count === 1 ? "" : "s"}</span>
            </span>
          )}
          {!isAdmin && (
            <button data-testid="feed-tip-button" onClick={() => onTip(post.id)} className="ml-auto flex items-center gap-2 text-sm text-[#D7263D] hover:text-[#B81D31]">
              <Gift className="w-5 h-5" /> Tip
            </button>
          )}
        </div>
      )}
      <AnimatePresence initial={false}>
        {open && !post.locked && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
            <Comments postId={post.id} onAdded={() => setComments((n) => n + 1)} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}
